import { camelCase, snakeCase } from 'change-case';
import { clsx, type ClassValue } from 'clsx';
import { FirebaseError } from 'firebase/app';
import { signOut as firebaseAuthSignOut } from 'firebase/auth';
import { twMerge } from 'tailwind-merge';
import { FIREBASE_AUTH_ERROR_MESSAGES } from '@/constants/firebase';
import { errorCatcher } from '@/lib/error-catcher';
import { firebaseAuth } from '@/lib/firebase/firebase-client';
import toastPopper from '@/lib/toast-popper';
import { clearFirebaseAdminAuthSession } from '@/server-actions/auth.action';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function signOut() {
  try {
    await firebaseAuthSignOut(firebaseAuth);
    // * Server action 清除 session
    await clearFirebaseAdminAuthSession();
  } catch (error: unknown) {
    if (error instanceof FirebaseError) {
      throw new FirebaseError(
        error.code,
        FIREBASE_AUTH_ERROR_MESSAGES[error.code as keyof typeof FIREBASE_AUTH_ERROR_MESSAGES]
      );
    } else {
      throw new Error((error as Error).message);
    }
  }
}

export function getPersonNameInitials(options: { name: string }) {
  return options.name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('');
}

export async function writeTextToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    toastPopper.success({ description: '複製成功' });
  } catch (error: unknown) {
    toastPopper.error({ description: '複製失敗' });
    errorCatcher(error);
  }
}

const convertKeys = (data: unknown, caseConverter: (key: string) => string): unknown => {
  if (Array.isArray(data)) {
    // * 如果是陣列，遞歸處理每個元素
    return data.map((item) => convertKeys(item, caseConverter));
  } else if (data && typeof data === 'object' && !Array.isArray(data)) {
    // * 如果是物件，遞歸處理每個鍵值對
    return Object.entries(data).reduce(
      (acc, [key, value]) => {
        acc[caseConverter(key)] = convertKeys(value, caseConverter);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  // * 如果是基本類型，直接返回
  return data;
};
// * Snake to Camel
export const snakeToCamel = (data: unknown) => convertKeys(data, camelCase);

// * Camel to Snake
export const camelToSnake = (data: unknown) => convertKeys(data, snakeCase);
